import React, { useState, useEffect } from 'react';
import { Modal } from '@/components/common';
import MaterialForm from '../MaterialForm';
import { useUIStore } from '@/store/uiStore';
import { materialApi } from '@/api/endpoints/materials';
import { CreateMaterialRequest, UpdateMaterialRequest, Material } from '@/types/material';

const CreateMaterialModal: React.FC = () => {

  const { modals, closeModal, selectedMaterial, refreshCallbacks } = useUIStore();
  const [loading, setLoading] = useState(false);
  const [material, setMaterial] = useState<Material | null>(null);
  const [formKey, setFormKey] = useState(Date.now()); // Add key to force form reset

  const isOpen = modals.createMaterial;
  const isEditing = !!selectedMaterial;

  // Fetch material data when editing
  useEffect(() => {
    if (isOpen && selectedMaterial) {
      fetchMaterial();
    } else if (isOpen && !selectedMaterial) {
      setMaterial(null);
    }
  }, [isOpen, selectedMaterial]);

  const fetchMaterial = async () => {
    if (!selectedMaterial) return;

    setLoading(true);
    try {
      const response = await materialApi.getMaterial(selectedMaterial);
      setMaterial(response);
    } catch (error) {
      console.error('Error fetching material:', error);
      alert('Không thể tải thông tin nguyên liệu. Vui lòng thử lại.');
      closeModal('createMaterial');
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (data: CreateMaterialRequest | UpdateMaterialRequest, andClose: boolean) => {
    setLoading(true);
    try {
      let message = '';
      if (isEditing && selectedMaterial) {
        const { code, ...updateData } = data as UpdateMaterialRequest;
        await materialApi.updateMaterial({ code: selectedMaterial, ...updateData });
        message = 'Cập nhật nguyên liệu thành công!';
      } else {
        await materialApi.createMaterial(data as CreateMaterialRequest);
        message = 'Tạo nguyên liệu thành công!';
      }
      alert(message);

      // Trigger refresh in parent component
      refreshCallbacks.materials?.();

      if (andClose) {
        closeModal('createMaterial');
      } else if (!isEditing) {
        // Reset form for new entry by changing key
        setFormKey(Date.now());
      }
    } catch (error) {
      console.error('Error saving material:', error);
      if ((error as any).message?.includes('Validation error')) {
        alert('Dữ liệu không hợp lệ. Vui lòng kiểm tra lại thông tin.');
      } else {
        const action = isEditing ? 'cập nhật' : 'tạo';
        alert(`Có lỗi xảy ra khi ${action} nguyên liệu. Vui lòng thử lại.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (data: CreateMaterialRequest | UpdateMaterialRequest) => {
    handleFormSubmit(data, false);
  };

  const handleSaveAndClose = (data: CreateMaterialRequest | UpdateMaterialRequest) => {
    handleFormSubmit(data, true);
  };

  const handleCancel = () => {
    closeModal('createMaterial');
    setMaterial(null);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title={isEditing ? "Chỉnh sửa nguyên liệu" : "Thêm nguyên liệu"} // Title positioned top-left of modal as specified
      size="xl" // Modal wrapper centered on screen as specified
      closeOnOverlayClick={!loading}
      closeOnEscape={!loading}
    >
      <MaterialForm
        key={formKey} // Use key to force re-mount
        material={material || undefined}
        onSubmit={handleSubmit as any}
        onSaveAndClose={handleSaveAndClose as any}
        onCancel={handleCancel}
        loading={loading}
      />
    </Modal>
  );
};

export default CreateMaterialModal;
